// Marketing → Drip detail (PR-C). Read-only summary + recent enrollments.

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import TopBar from '../../components/TopBar.jsx';
import { getDrip, listDripEnrollments } from '../../api/marketingDrips.js';

export default function DripDetail() {
  const { id } = useParams();
  const { data } = useQuery({
    queryKey: ['marketing-drip', id],
    queryFn: () => getDrip(id),
    enabled: !!id,
  });
  const { data: enrData } = useQuery({
    queryKey: ['marketing-drip-enrollments', id],
    queryFn: () => listDripEnrollments(id, { limit: 50 }),
    enabled: !!id,
  });

  const drip = data?.drip;
  const enrollments = enrData?.enrollments || [];

  return (
    <div className="space-y-4">
      <TopBar
        title={drip?.name || 'Drip'}
        subtitle={drip?.description || ''}
      />
      <div className="text-sm">
        <Link to="/marketing/drips" className="text-[#3B82F6] hover:underline">← Back to Drips</Link>
      </div>

      {!drip ? (
        <div className="panel p-6 text-sm text-white/60">Loading…</div>
      ) : (
        <>
          <div className="panel p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Stat label="Enrolled" value={drip.enrollCount || 0} />
            <Stat label="Completed" value={drip.completeCount || 0} />
            <Stat label="Stopped" value={drip.stopCount || 0} />
            <Stat label="Steps" value={drip.steps?.length || 0} />
          </div>

          <div className="panel p-4">
            <div className="font-medium mb-2 text-sm">Steps</div>
            <ol className="space-y-2">
              {drip.steps?.map((s, i) => (
                <li key={i} className="rounded-md border border-white/10 bg-white/2 p-2">
                  <div className="text-xs text-white/50">Step {i + 1} · {s.delayMinutes} min{i === 0 ? ' from start' : ' after previous'}</div>
                  <div className="text-sm font-mono whitespace-pre-wrap">{s.body || (s.presetTextId ? `(preset ${s.presetTextId})` : '—')}</div>
                </li>
              ))}
            </ol>
          </div>

          <div className="panel p-0 overflow-hidden">
            <div className="p-3 border-b border-white/10 font-medium text-sm">Recent enrollments</div>
            <table className="w-full text-sm">
              <thead className="text-xs text-white/50 bg-white/2">
                <tr>
                  <th className="text-left px-4 py-2">Lead</th>
                  <th className="text-left px-4 py-2 w-32">Status</th>
                  <th className="text-right px-4 py-2 w-20">Step</th>
                  <th className="text-left px-4 py-2 w-40">Started</th>
                  <th className="text-left px-4 py-2 w-40">Next send</th>
                </tr>
              </thead>
              <tbody>
                {enrollments.length === 0 ? (
                  <tr><td colSpan={5} className="p-4 text-center text-white/50">No enrollments yet.</td></tr>
                ) : enrollments.map((e) => (
                  <tr key={e._id} className="border-t border-white/5">
                    <td className="px-4 py-2">
                      {e.lead ? `${e.lead.firstName || ''} ${e.lead.lastName || ''}`.trim() || e.lead.phone : e.lead}
                    </td>
                    <td className="px-4 py-2 text-white/70">{e.status}</td>
                    <td className="px-4 py-2 text-right">{e.currentStepIndex}</td>
                    <td className="px-4 py-2 text-white/60">{e.startedAt ? new Date(e.startedAt).toLocaleString() : '—'}</td>
                    <td className="px-4 py-2 text-white/60">{e.nextSendAt ? new Date(e.nextSendAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-white/10 p-3">
      <div className="text-xs text-white/50">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  );
}
